import { useMemo, useState } from 'react'
import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material'
import type { DashboardMetrics } from 'store/dashboard/dashboard.types'
import { ACCENT_BLUE } from 'utils/brand-colors'

type Period = 'week' | 'month' | 'year'

type Props = {
  metrics?: DashboardMetrics
  loading?: boolean
}

const CHART_BLUE = ACCENT_BLUE
const MUTED = '#6B7280'
const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_LABELS = ['W1', 'W2', 'W3', 'W4']
const YEAR_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Distinct upward shapes for each period (relative weights). */
const SHAPE_WEIGHTS: Record<Period, number[]> = {
  // Mid-week dip, strong Fri finish, softer weekend
  week: [0.42, 0.55, 0.48, 0.68, 0.92, 0.78, 1],
  // Upward overall, with a mid-month dip so it isn't a straight line
  month: [0.38, 0.72, 0.55, 1],
  // Year-long rise with small seasonal dips (Mar / Jun / Sep)
  year: [0.28, 0.36, 0.32, 0.45, 0.58, 0.52, 0.68, 0.78, 0.72, 0.86, 0.94, 1],
}

const amount = (m?: { totalAmount?: number }) =>
  typeof m?.totalAmount === 'number' ? m.totalAmount : Number(m?.totalAmount ?? 0)

const formatCurrency = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const smoothCurve = (pts: { x: number; y: number }[]): string => {
  if (pts.length === 0) return ''
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(i + 2, pts.length - 1)]
    d += ` C ${(p1.x + (p2.x - p0.x) / 6).toFixed(2)} ${(p1.y + (p2.y - p0.y) / 6).toFixed(2)}, ${(p2.x - (p3.x - p1.x) / 6).toFixed(2)} ${(p2.y - (p3.y - p1.y) / 6).toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
  }
  return d
}

const buildPoints = (period: Period, metrics?: DashboardMetrics) => {
  const rev = metrics?.revenue
  let total = 0
  let labels = WEEK_LABELS

  if (period === 'week') {
    total = amount(rev?.thisWeek)
    labels = WEEK_LABELS
  } else if (period === 'month') {
    total = amount(rev?.thisMonth)
    labels = MONTH_LABELS
  } else {
    total = amount(rev?.thisYear)
    labels = YEAR_LABELS
  }

  const weights = SHAPE_WEIGHTS[period]
  const sum = weights.reduce((a, b) => a + b, 0)
  // Keep shape visible even when total is 0 (pure dummy plot)
  const scale = total > 0 ? total : 1000

  return {
    total,
    points: labels.map((label, i) => ({
      label,
      amount: Math.round((scale * weights[i]) / sum),
    })),
  }
}

const FleetRevenueChart = ({ metrics, loading }: Props) => {
  const [period, setPeriod] = useState<Period>('week')
  const { total, points } = useMemo(() => buildPoints(period, metrics), [period, metrics])

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: MUTED, fontSize: 14 }}>Loading…</Typography>
      </Box>
    )
  }

  const W = 100
  const H = 52
  const PAD = { t: 8, r: 2, b: 4, l: 2 }
  const innerW = W - PAD.l - PAD.r
  const innerH = H - PAD.t - PAD.b
  const amounts = points.map((p) => p.amount)
  const minY = Math.min(...amounts)
  const maxY = Math.max(...amounts, 1)
  // Give headroom so the upward curve doesn't hug the top/bottom
  const range = Math.max(maxY - minY, 1)
  const coords = points.map((p, i) => ({
    x: PAD.l + (i / Math.max(points.length - 1, 1)) * innerW,
    y: PAD.t + innerH - ((p.amount - minY) / range) * innerH * 0.92,
    label: p.label,
  }))
  const curve = smoothCurve(coords)
  const baseline = PAD.t + innerH
  const area = `${curve} L ${coords[coords.length - 1]?.x.toFixed(2)} ${baseline} L ${coords[0]?.x.toFixed(2)} ${baseline} Z`

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{formatCurrency(total)}</Typography>
        <FormControl size="small" sx={{ minWidth: 116 }}>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            sx={{ borderRadius: 1.5, fontSize: 13, height: 32 }}
          >
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="year">This Year</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ position: 'relative', height: 120, width: '100%', minWidth: 0 }}>
        <Box
          component="svg"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          sx={{ width: '100%', height: '100%', display: 'block' }}
        >
          <defs>
            <linearGradient id={`fleet-rev-fill-${period}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_BLUE} stopOpacity={0.28} />
              <stop offset="100%" stopColor={CHART_BLUE} stopOpacity={0} />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1={PAD.l}
              x2={W - PAD.r}
              y1={PAD.t + innerH * (1 - f)}
              y2={PAD.t + innerH * (1 - f)}
              stroke="#E5E7EB"
              strokeWidth={0.35}
              strokeDasharray="1.2 1.2"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <path d={area} fill={`url(#fleet-rev-fill-${period})`} />
          <path
            d={curve}
            fill="none"
            stroke={CHART_BLUE}
            strokeWidth={2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </Box>
      </Box>

      <Box sx={{ position: 'relative', height: 18, width: '100%', mt: 0.75 }}>
        {coords.map((c) => (
          <Typography
            key={c.label}
            sx={{
              position: 'absolute',
              left: `${(c.x / W) * 100}%`,
              transform: 'translateX(-50%)',
              fontSize: 11,
              fontWeight: 600,
              color: MUTED,
              whiteSpace: 'nowrap',
            }}
          >
            {c.label}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}

export default FleetRevenueChart
